import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as dailyReportActions from '@/components/dailyReport/Actions';
import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as doubleTabsModalActions from '@/components/modals/doubleTabsModal/Actions';
import * as educationCheckActions from '@/components/modals/mistakeIntervention/Actions';
import * as offlineRetryModalActions from '@/components/modals/offlineRetryModal/Actions';
import * as onboardingMessagesActions from '@/components/onboardingMessages/Actions';
import * as freePlayActions from '@/pages/freePlay/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as playlistActions from '@/playlist/Actions';
import * as sharedActions from '@/shared/Actions';
import { ONBOARDING_MESSAGES_TYPES } from '@/components/onboardingMessages/Constants';
import { playlistActionTypes } from '@/playlist/Constants';
import {
	finishFreePlaySectionApiPath,
	finishLearningPathSectionApiPath,
	getExerciseApiPath,
	getFreePlayExerciseApiPath,
	getStoreTimePath,
	getUpdateUserPath,
	ROUTES_API,
} from '@/services/Constants';
import { checkDailyGoal, checkGameSection } from '@/pages/learningPath/Helper';
import { getClientDeviceId, getExercisePath, isOfflineError } from '@/services/Helper';
import { bridgeActions } from '@/features/bridge';
import { getSteps } from '@/pages/learningPath/Selectors';
import { getPlayListSections } from '@/pages/report/Selectors';
import {
	getBridgeRoom,
	getBridgeTask,
	getIsBridgePlaylist,
	getPersonalityQuizAnswerIds,
	getUserId,
} from '@/playlist/Selectors';
import { EndOutcome } from '@/playlist/types';
import { processRequest } from '@/services/Api';
import { historyPush } from '@/services/History';
import { checkVersionAndReload } from '@/services/VersionChecker';

import type { SagaIterator } from 'redux-saga';

// ============================================================================
// Root Saga
// ============================================================================

export default function* (): Generator {
	yield all([
		// Playlist fetching
		takeLatest(playlistActionTypes.GET_PLAYLIST_REQUEST, handleGetPlaylistRequest),
		takeLatest(playlistActionTypes.GET_PLAYLIST_REPORT_REQUEST, handleGetPlaylistReportRequest),
		takeLatest(playlistActionTypes.GET_PLAYLIST_REPLAY_REQUEST, handleGetPlaylistReplayRequest),

		// Question explanations
		takeLatest(
			playlistActionTypes.GET_QUESTIONS_EXPLANATIONS,
			handleQuestionsExplanationsRequest,
		),

		// Answer submission
		takeLatest(playlistActionTypes.SEND_ANSWER_REQUEST, handleSendAnswerRequest),

		// Playlist completion
		takeLatest(playlistActionTypes.FINISH_PLAYLIST_REQUEST, handleFinishPlaylistRequest),

		// Instruction feedback
		takeLatest(
			playlistActionTypes.SEND_TOPIC_INSTRUCTION_FEEDBACK,
			handleSendTopicInstructionFeedback,
		),
		takeLatest(
			playlistActionTypes.SEND_QUESTION_INSTRUCTION_FEEDBACK,
			handleSendQuestionInstructionFeedback,
		),

		// Time tracking
		takeLatest(playlistActionTypes.STORE_USER_QUESTION_TIME, handleStoreTime),

		// Audio preferences
		takeLatest(
			playlistActionTypes.SAVE_AUDIO_PREFERENCES_REQUEST,
			handleSaveAudioPreferencesRequest,
		),

		// Mistake intervention
		takeLatest(
			playlistActionTypes.SEND_MISTAKE_INTERVENTION_REQUEST,
			handleSendMistakeInterventionRequest,
		),
		takeLatest(
			playlistActionTypes.RESET_MISTAKE_INTERVENTION_REQUEST,
			handleResetMistakeInterventionRequest,
		),

		// WebSocket
		takeLatest(
			playlistActionTypes.RECEIVED_LAST_ACTIVE_DEVICE_CHANNEL_STREAM,
			handleReceivedLastActiveDeviceChannelStream,
		),
	]);
}

// ============================================================================
// Playlist Fetching
// ============================================================================

/**
 * Unified handler for fetching playlist data (both regular exercises and games)
 */
export function* handleGetPlaylistRequest({ payload = {} }: any): SagaIterator {
	try {
		const { id, freePlayCategoryId } = payload;
		const exerciseApiPath = freePlayCategoryId
			? getFreePlayExerciseApiPath(freePlayCategoryId, id)
			: getExerciseApiPath(id);

		const { data } = yield call(processRequest, exerciseApiPath, 'GET', {}, false, false, {
			'X-Client-Device': getClientDeviceId(),
		});

		// Check if a new version has been deployed and reload if necessary
		yield call(checkVersionAndReload);

		yield put(playlistActions.getPlaylistSuccess(data.user_learning_path_section));

		// update learning path if this playlist is a game section, the next tile will be unlocked
		if (data.user_learning_path_section.game?.type) {
			yield put(learningPathActions.setLearningPathUpdated(true));
		}

		// Fetch question explanations for non-completed playlists
		if (!data.user_learning_path_section.date_played) {
			yield put(
				playlistActions.getQuestionsExplanations({
					questionIds: data.user_learning_path_section.questions.map(
						(question: any) => question.id,
					),
				}),
			);
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(playlistActions.getPlaylistError());
	}
}

export function* handleGetPlaylistReportRequest({ payload = {} }: any): SagaIterator {
	try {
		const { id } = payload;
		const playListSections = yield select(getPlayListSections);

		if (playListSections[id]) {
			yield put(playlistActions.getPlaylistReportSuccess(playListSections[id]));
			return;
		}

		const { data } = yield call(processRequest, getExerciseApiPath(id), 'GET', {
			preview: true,
		});

		yield put(playlistActions.getPlaylistReportSuccess(data.user_learning_path_section));
		yield put(
			playlistActions.getQuestionsExplanations({
				questionIds: data.user_learning_path_section.questions.map(
					(question: any) => question.id,
				),
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(playlistActions.getPlaylistReportError());
	}
}

export function* handleGetPlaylistReplayRequest({ payload = {} }: any): SagaIterator {
	try {
		const { id } = payload;

		const { data } = yield call(processRequest, getExerciseApiPath(id), 'GET', {
			replay: true,
		});

		const playList = { ...data.user_learning_path_section };
		playList.questions_count = playList.questions.length;
		playList.correct_answers_count = 0;
		playList.wrong_answers_count = 0;

		yield put(playlistActions.getPlaylistReplaySuccess(playList));
		yield put(
			playlistActions.getQuestionsExplanations({
				questionIds: data.user_learning_path_section.questions.map(
					(question: any) => question.id,
				),
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(playlistActions.getPlaylistReplayError());
	}
}

// ============================================================================
// Question Explanations
// ============================================================================

export function* handleQuestionsExplanationsRequest({ payload }: any): SagaIterator {
	try {
		const { questionIds } = payload;
		// Avoid making request if questionIds is empty
		if (!questionIds || questionIds.length === 0) {
			return;
		}

		const { data } = yield call(processRequest, ROUTES_API.QUESTIONS_EXPLANATIONS, 'GET', {
			question_ids: questionIds,
		});

		yield put(
			playlistActions.getQuestionsExplanationsSuccess({
				data: data,
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// ============================================================================
// Answer Submission
// ============================================================================

/**
 * Unified handler for sending answers (both regular exercises and games)
 */
export function* handleSendAnswerRequest({ payload }: any): SagaIterator {
	const {
		playlistId,
		freePlayCategoryId,
		data,
		lastQuestion,
		callback,
		playtimeInSeconds,
		isCorrect,
	} = payload || {};

	const skipAnswerRequests: boolean = yield select(
		(state: any) => state.playlistReducer.skipAnswerRequests,
	);
	const skipFinishRequest: boolean = yield select(
		(state: any) => state.playlistReducer.skipFinishRequest,
	);

	if (skipAnswerRequests) {
		yield put(playlistActions.sendAnswerSuccess());
		if (lastQuestion) {
			if (skipFinishRequest) {
				yield put(playlistActions.finishPlaylistSuccess());
			} else {
				yield put(playlistActions.finishPlaylistRequest(playlistId, freePlayCategoryId));
			}
		}
		return;
	}

	try {
		const { data: responseData } = yield call(
			processRequest,
			ROUTES_API.QUESTION_ANSWER,
			'POST',
			data,
			false,
			false,
			{ 'X-Client-Device': getClientDeviceId() },
		);

		// Store playtime if provided (for games)
		const answerId = responseData?.answer;
		const playtimeNumeric = Number(playtimeInSeconds);
		if (answerId != null && Number.isFinite(playtimeNumeric) && playtimeNumeric >= 0) {
			yield call(
				processRequest,
				getStoreTimePath(answerId),
				'PUT',
				{ playtime_in_seconds: playtimeInSeconds },
				false,
				false,
				{ 'X-Client-Device': getClientDeviceId() },
			);
		}

		yield put(playlistActions.sendAnswerSuccess());

		yield put(offlineRetryModalActions.closeOfflineRetryModal());

		// Update tile progress status for learning path (not free play)
		if (!freePlayCategoryId && isCorrect !== undefined) {
			yield put(learningPathActions.updateTileProgressStatus(String(playlistId), isCorrect));
		}

		// Handle mistake intervention (for exercises)
		if (responseData?.kc_intervention_id) {
			yield put(educationCheckActions.toggleEducationCheckModal(true));
			yield put(
				educationCheckActions.setMistakeInterventionId(responseData?.kc_intervention_id),
			);
		}

		callback && callback(responseData);

		// Finish playlist if this was the last question (for exercises)
		if (lastQuestion) {
			yield put(playlistActions.finishPlaylistRequest(playlistId, freePlayCategoryId));
		}
	} catch (e) {
		if (isOfflineError(e)) {
			yield put(
				offlineRetryModalActions.openOfflineRetryModal(() =>
					playlistActions.sendAnswerRequest({
						playlistId,
						freePlayCategoryId,
						data,
						lastQuestion,
						callback,
						playtimeInSeconds,
						isCorrect,
					}),
				),
			);
		} else {
			yield put(errorHandlerActions.handleError(e, 'notification'));
		}
		yield put(playlistActions.sendAnswerError());
	}
}

// ============================================================================
// Playlist Completion
// ============================================================================

function buildFinishRequestBody(
	gameOutcome: string | undefined,
	score: number | undefined,
	isDailyGoalTile: boolean,
): Record<string, any> {
	if (gameOutcome) {
		return { game_outcome: gameOutcome, ...(score !== undefined && { score }) };
	}
	if (isDailyGoalTile) {
		return { daily_goal: true };
	}
	return {};
}

function* dispatchOnboardingMessages(data: any, isGameSection: boolean): SagaIterator {
	if (data.show_onboarding_activity_report_message) {
		yield put(
			onboardingMessagesActions.setOnboardingMessagesType(
				ONBOARDING_MESSAGES_TYPES.USER_REPORT,
			),
		);
	}
	if (data.show_first_daily_goal_message) {
		yield put(
			onboardingMessagesActions.setOnboardingMessagesType(
				ONBOARDING_MESSAGES_TYPES.WEEK_GOAL,
			),
		);
	}
	if (isGameSection) {
		yield put(
			onboardingMessagesActions.setOnboardingMessagesType(
				ONBOARDING_MESSAGES_TYPES.LEARNING_PATH_GAME_COMPLETED,
			),
		);
	}
}

function* handleFinishBridgePlaylist(
	gameOutcome: string | undefined,
	score: number | undefined,
): SagaIterator {
	const bridgeRoom = yield select(getBridgeRoom);
	const bridgeTask = yield select(getBridgeTask);

	if (!bridgeRoom || !bridgeTask) {
		yield put(playlistActions.finishPlaylistError());
		return;
	}

	const personalityQuizAnswerIds: number[] = yield select(getPersonalityQuizAnswerIds);
	yield put(bridgeActions.finishTaskRequest(gameOutcome, score, personalityQuizAnswerIds));
	yield put(playlistActions.finishPlaylistSuccess());
}

function* handleFinishRegularPlaylist(
	playlistId: any,
	freePlayCategoryId: any,
	gameOutcome: string | undefined,
	score: number | undefined,
): SagaIterator {
	const steps = yield select(getSteps);
	const isDailyGoalTile = checkDailyGoal(playlistId, steps);
	const isGameSection = checkGameSection(playlistId, steps);

	const finishApiPath = freePlayCategoryId
		? finishFreePlaySectionApiPath(freePlayCategoryId, playlistId)
		: finishLearningPathSectionApiPath(playlistId);

	const requestBody = buildFinishRequestBody(gameOutcome, score, isDailyGoalTile);

	const { data } = yield call(processRequest, finishApiPath, 'POST', requestBody, false, false, {
		'X-Client-Device': getClientDeviceId(),
	});

	if (data.learning_path_updated) {
		yield put(learningPathActions.setLearningPathUpdated(true));
	}
	if (freePlayCategoryId) {
		yield put(freePlayActions.setLevelProgress(data));
	}
	if (data.level_up && data.relative_difficulty) {
		yield put(playlistActions.setLevelUp(data.relative_difficulty));
	}

	yield call(dispatchOnboardingMessages, data, isGameSection);

	yield put(dailyReportActions.getDailyReportRequest());
	yield put(playlistActions.finishPlaylistSuccess());

	if (!freePlayCategoryId && gameOutcome === EndOutcome.LOST) {
		yield put(learningPathActions.finishTileProgress(String(playlistId)));
	}
	if (!freePlayCategoryId) {
		yield put(learningPathActions.setFinishTileAnimation(true));
	}
}

export function* handleFinishPlaylistRequest({ payload }: any): SagaIterator {
	const { playlistId, freePlayCategoryId, gameOutcome, score } = payload || {};
	const isBridgePlaylist: boolean = yield select(getIsBridgePlaylist);

	try {
		if (isBridgePlaylist) {
			yield call(handleFinishBridgePlaylist, gameOutcome, score);
		} else {
			yield call(
				handleFinishRegularPlaylist,
				playlistId,
				freePlayCategoryId,
				gameOutcome,
				score,
			);
		}
	} catch (e) {
		yield put(playlistActions.finishPlaylistError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// ============================================================================
// Instruction Feedback
// ============================================================================

export function* handleSendTopicInstructionFeedback({ payload }: any): SagaIterator {
	const { isUseful, instructionId, questionId } = payload || {};
	try {
		const instruction_feedback = {
			instruction_id: instructionId,
			question_id: questionId,
			feedback: isUseful,
		};
		yield call(processRequest, ROUTES_API.INSTRUCTION_FEEDBACK, 'POST', instruction_feedback);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

export function* handleSendQuestionInstructionFeedback({ payload }: any): SagaIterator {
	const { feedback, questionId } = payload || {};
	try {
		const instruction_feedback = {
			question_id: questionId,
			feedback: feedback,
		};
		yield call(processRequest, ROUTES_API.INSTRUCTION_FEEDBACK, 'POST', instruction_feedback);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

// ============================================================================
// Time Tracking
// ============================================================================

export function* handleStoreTime({ payload }: any): SagaIterator {
	const { answer_id, playtime_in_seconds } = payload || {};
	try {
		yield call(
			processRequest,
			getStoreTimePath(answer_id),
			'PUT',
			{
				playtime_in_seconds,
			},
			false,
			false,
			{ 'X-Client-Device': getClientDeviceId() },
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

// ============================================================================
// Audio Preferences
// ============================================================================

export function* handleSaveAudioPreferencesRequest({ payload }: any): SagaIterator {
	try {
		const userId = yield select(getUserId);
		const { audioOn, audioOnboarded } = payload;

		const data: Record<string, any> = { audio_on: audioOn };
		if (audioOnboarded !== undefined) {
			data.onboarding_question_instruction = audioOnboarded;
		}

		yield call(processRequest, getUpdateUserPath(userId), 'PUT', {
			user: data,
		});

		yield put(sharedActions.getUserDataRequest());

		yield put(playlistActions.saveAudioPreferencesSuccess());
	} catch (e) {
		yield put(playlistActions.saveAudioPreferencesError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// ============================================================================
// Mistake Intervention
// ============================================================================

export function* handleSendMistakeInterventionRequest({ payload }: any): SagaIterator {
	const { playlistId, mistakeInterventionId } = payload || {};
	try {
		const { data } = yield call(
			processRequest,
			`user_knowledge_components/${mistakeInterventionId}/create_intervention`,
			'POST',
			{ user_learning_path_section_id: playlistId },
		);

		if (data.user_learning_path_section_id && data.title) {
			yield put(playlistActions.clearState());
			yield call(
				historyPush,
				getExercisePath(data.title, data.user_learning_path_section_id),
			);
			yield put(learningPathActions.setLearningPathUpdated(true));
		}

		yield put(playlistActions.sendMistakeInterventionSuccess());
	} catch (e) {
		yield put(playlistActions.sendMistakeInterventionError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleResetMistakeInterventionRequest({ payload }: any): SagaIterator {
	const { mistakeInterventionId } = payload || {};
	try {
		yield call(
			processRequest,
			`user_knowledge_components/${mistakeInterventionId}/reset_intervention`,
			'PUT',
		);
		yield put(playlistActions.resetMistakeInterventionSuccess());
	} catch (e) {
		yield put(playlistActions.resetMistakeInterventionError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

// ============================================================================
// WebSocket
// ============================================================================

export function* handleReceivedLastActiveDeviceChannelStream({ payload }: any): SagaIterator {
	const { data } = payload || {};
	try {
		const deviceId = getClientDeviceId();
		if (data.active_device_id && data.active_device_id !== deviceId) {
			yield put(doubleTabsModalActions.toggleDoubleTabsModal(true));
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
	}
}
